import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';

import {
  CareerFeedComponent,
  GetCareerFeedDocument,
  type CareerFeedJob,
} from 'src/graphql/graphql';
import { useTimezone } from 'src/hooks/useTimezone';
import {
  useIsHomeFeedAPIEnabled,
  useIsHomepageGenAIChatKillSwitchEnabled,
} from 'src/store/featureFlags/hooks';
import { useSelector } from 'src/store/react';
import { selectServerAuthenticated } from 'src/store/selectors';
import type { ApolloCacheType } from 'src/types/apollo';
import { useMelwaysLocale, useMelwaysZone } from 'src/utils/melwaysHelper';

import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';

import { mapQueryToCareerFeedItem } from './feed-types';
import { useVisitorId } from './useHomeRecommendations';

export const useCareerFeed = () => {
  const locale = useMelwaysLocale();
  const zone = useMelwaysZone();
  const visitorId = useVisitorId();
  const isServerAuthenticated = useSelector(selectServerAuthenticated);
  const timezone = useTimezone();
  const isFeedEnabled = useIsHomeFeedAPIEnabled();
  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();
  // This will make suer that the initial chat context is only used once, and the recs will not be updated when the context changes
  // When the context changed, we will call the `refresh()` separately to get the new recs on the AI Assistant
  const [initialChatContext] = useState(persistedCandidateChatContext ?? null);
  const isHomepageGenAIChatKillSwitchEnabled =
    useIsHomepageGenAIChatKillSwitchEnabled();

  const { data, error, loading } = useQuery(GetCareerFeedDocument, {
    errorPolicy: 'all',
    variables: {
      locale,
      zone,
      timezone,
      visitorId,
      components: [CareerFeedComponent.Job],
      candidateChatContext: isHomepageGenAIChatKillSwitchEnabled
        ? null
        : initialChatContext,
    },
    ssr: isServerAuthenticated && Boolean(visitorId),
    skip: !isFeedEnabled,
  });

  const feedItems = useMemo(() => mapQueryToCareerFeedItem(data), [data]);

  return {
    feedItems,
    error,
    loading,
    tracking: {
      solMetadata: data?.careerFeed?.solMetadata,
    },
  };
};

export const useUpdateCareerFeedSavedJobCache = () => (jobId?: string) => {
  if (!jobId) {
    return undefined;
  }

  const update = (isSaved: boolean) => (cache: ApolloCacheType) => {
    const targetJob = cache.identify({
      jobId,
      __typename: 'CareerFeedJob',
    });

    if (!targetJob) {
      return;
    }

    cache.modify<CareerFeedJob>({
      id: targetJob,
      fields: {
        savedJob() {
          return isSaved ? { id: jobId } : null;
        },
      },
    });
  };

  return { setSaved: update(true), setUnsave: update(false) };
};
